import Vue from 'vue'
import { MessageBox } from 'element-ui'
import { UserModule } from '@/store/modules/user'
import { dispatchStoreContextChanged, getFilterStoreId } from '@/utils/storeContext'

/** 详情接口数据优先于列表行（列表在跨店筛选时 row.storeId 可能不准） */
export function resolveOrderStoreId(row?: any, diaForm?: any): number | null {
  const form = diaForm && !Array.isArray(diaForm) ? diaForm : null
  if (form && form.storeId != null) return Number(form.storeId)
  if (row && row.storeId != null) return Number(row.storeId)
  return null
}

export function resolveOrderStoreName(
  orderStoreId: number | null,
  row?: any,
  diaForm?: any,
  nameMap?: Record<string, string>,
): string {
  const form = diaForm && !Array.isArray(diaForm) ? diaForm : null
  if (form && form.shopName) return form.shopName
  if (row && row.shopName) return row.shopName
  if (orderStoreId != null && nameMap && nameMap[String(orderStoreId)]) {
    return nameMap[String(orderStoreId)]
  }
  if (orderStoreId != null) return `商户${orderStoreId}`
  return '对应商户'
}

export function resolveOrderId(
  row?: any,
  diaForm?: any,
  orderId?: string | number | null,
): string | number | null {
  if (orderId != null && orderId !== '') return orderId
  if (row && row.id != null) return row.id
  const form = diaForm && !Array.isArray(diaForm) ? diaForm : null
  if (form && form.id != null) return form.id
  return null
}

export function resolveOrderStatus(row?: any, diaForm?: any, fallback = 2): number {
  const form = diaForm && !Array.isArray(diaForm) ? diaForm : null
  if (form && form.status != null) return Number(form.status)
  if (row && row.status != null) return Number(row.status)
  return fallback
}

/** 接单/拒单前从详情接口读取订单真实所属店铺（避免误用顶栏或列表行的 storeId） */
export async function loadAuthoritativeOrderContext(orderId: string | number) {
  const { queryOrderDetailById } = await import('@/api/order')
  const res = await queryOrderDetailById({ orderId })
  const body = res && res.data
  if (!body || Number(body.code) !== 1 || !body.data) return null
  const detail = body.data
  const sid = detail.storeId != null ? Number(detail.storeId) : null
  return {
    orderId: detail.id != null ? detail.id : orderId,
    orderStoreId: sid,
    orderStoreName:
      detail.shopName || (sid != null ? `商户${sid}` : '对应商户'),
    orderStatus: detail.status != null ? Number(detail.status) : 2,
    detail,
  }
}

export function buildOrderOperationContext(options: {
  row?: any
  diaForm?: any
  orderId?: string | number | null
  dialogOrderStatus?: number
  orderStatus?: number
  storeNameMap?: Record<string, string>
}) {
  const target = options.row
  const diaForm = options.diaForm
  const orderId = resolveOrderId(target, diaForm, options.orderId)
  const orderStoreId = resolveOrderStoreId(target, diaForm)
  const orderStoreName = resolveOrderStoreName(
    orderStoreId,
    target,
    diaForm,
    options.storeNameMap,
  )
  const orderStatus = resolveOrderStatus(
    target,
    diaForm,
    options.dialogOrderStatus != null
      ? options.dialogOrderStatus
      : options.orderStatus != null
        ? options.orderStatus
        : 2,
  )
  return { orderId, orderStoreId, orderStoreName, orderStatus, target }
}

export function isOrderStoreMismatchMsg(msg: string | undefined | null): boolean {
  if (msg == null || msg === '') return false
  return String(msg).indexOf('不属于当前商户') !== -1
}

/**
 * 订单店铺不匹配：直接弹窗引导切换顶栏店铺（用于接口 code=0 且 msg 含「不属于当前商户」）
 */
export async function promptSwitchStoreForOrder(
  orderId: string | number,
  router?: any,
): Promise<boolean> {
  const auth = await loadAuthoritativeOrderContext(orderId)
  if (!auth || auth.orderStoreId == null) {
    try {
      await MessageBox.alert(
        '无法获取该订单所属店铺，请在顶栏「切换商户」中手动选择正确店铺后重试。',
        '切换店铺',
        { type: 'warning', confirmButtonText: '知道了' },
      )
    } catch {
      /* cancel */
    }
    return false
  }

  const current = getFilterStoreId()
  const storeName = auth.orderStoreName
  const targetId = auth.orderStoreId

  if (current && String(targetId) === current) {
    try {
      await MessageBox.alert(
        `当前顶栏已是「${storeName}」，但提交参数可能有误。请关闭弹窗后重新打开订单再操作。`,
        '提示',
        { type: 'warning', confirmButtonText: '知道了' },
      )
    } catch {
      /* */
    }
    return false
  }

  try {
    await MessageBox.confirm(
      `该订单属于「${storeName}」，与当前所选商户不一致。\n\n是否切换到该店铺？`,
      '切换店铺',
      {
        confirmButtonText: '切换店铺',
        cancelButtonText: '取消',
        type: 'warning',
        closeOnClickModal: false,
      },
    )
    await UserModule.SetStore(String(targetId))
    dispatchStoreContextChanged({
      storeId: String(targetId),
      storeName,
    })
    if (router) {
      await Vue.nextTick()
      await openOrderInManagement(router, auth.orderId, auth.orderStatus)
    }
    return true
  } catch {
    return false
  }
}

/** 跳转到订单管理并打开指定订单 */
export async function openOrderInManagement(
  router: any,
  orderId: string | number,
  orderStatus = 2,
) {
  const query = {
    orderId: String(orderId),
    status: String(orderStatus),
  }
  const cur = router.currentRoute || {}

  if (cur.path === '/order') {
    await router.replace({ path: '/order', query }).catch(() => {})
  } else {
    await router.push({ path: '/order', query }).catch(() => {})
  }
}

/**
 * 顶栏商户与订单所属商户不一致时提示切换。
 * 全平台（未选店）时也会提示，避免用错误 storeId 调接口。
 */
export async function ensureOrderStoreContext(options: {
  orderStoreId: number | null
  orderStoreName?: string
  mode?: 'continue' | 'openOrder'
  router?: any
  orderId?: string | number | null
  orderStatus?: number
  /** 接口已报店铺不匹配时传 true，避免「顶栏店与 ctx 相同」却未真正匹配订单店 */
  forceSwitch?: boolean
}): Promise<boolean> {
  const {
    orderStoreId,
    orderStoreName = '对应商户',
    mode = 'continue',
    router,
    orderId,
    orderStatus = 2,
    forceSwitch = false,
  } = options
  if (orderStoreId == null) return true

  const current = getFilterStoreId()
  if (!forceSwitch && current && String(orderStoreId) === current) return true

  const openOrder = mode === 'openOrder' && router && orderId != null
  const confirmText = openOrder ? '切换并打开订单' : '切换并继续'
  const message = openOrder
    ? `此订单属于「${orderStoreName}」，与当前所选商户不一致。是否切换到该商户并打开此订单？`
    : `此订单属于「${orderStoreName}」，与当前所选商户不一致。是否切换到该商户后继续操作？`

  try {
    await MessageBox.confirm(message, '切换商户', {
      confirmButtonText: confirmText,
      cancelButtonText: '取消',
      type: 'warning',
    })
    await UserModule.SetStore(String(orderStoreId))
    dispatchStoreContextChanged({
      storeId: String(orderStoreId),
      storeName: orderStoreName,
    })
    if (openOrder) {
      await Vue.nextTick()
      await openOrderInManagement(router, orderId as string | number, orderStatus)
    }
    return true
  } catch {
    return false
  }
}

/** 接口返回店铺不匹配：切换商户并进入对应订单 */
export async function handleOrderStoreMismatch(options: {
  msg: string | undefined
  orderStoreId: number | null
  orderStoreName?: string
  router: any
  orderId: string | number | null
  orderStatus?: number
}): Promise<boolean> {
  const {
    msg,
    orderStoreId,
    orderStoreName,
    router,
    orderId,
    orderStatus = 2,
  } = options
  if (!isOrderStoreMismatchMsg(msg)) return false

  if (orderStoreId == null) return false

  if (orderId != null) {
  return ensureOrderStoreContext({
    orderStoreId,
    orderStoreName,
    mode: 'openOrder',
    router,
    orderId,
    orderStatus,
    forceSwitch: true,
  })
}

  return ensureOrderStoreContext({
    orderStoreId,
    orderStoreName,
    mode: 'continue',
  })
}
